import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../CSS/CRUDfiles.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const STATUS_SEARCH_FIELDS = ['status', 'description'];
const STATUS_SORT_OPTIONS = [
  { key: 'status', label: 'Status', accessor: (s) => s.status?.toLowerCase() ?? '' },
];

const RecordStatusComponent = () => {
  const [statuses, setStatuses] = useState([]);
  const [currentStatus, setCurrentStatus] = useState({ status: '', color: '', description: '' });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const { authState } = useAuth();
  const navigate = useNavigate();

  const {
    items: visibleStatuses,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(statuses, {
    searchFields: STATUS_SEARCH_FIELDS,
    sortOptions: STATUS_SORT_OPTIONS,
  });

  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all record statuses
  useEffect(() => {
    fetchRecordStatuses();
  }, []);

  const fetchRecordStatuses = async () => {
    try {
      const response = await fetch(`${backendURL}/api/record-status/`, {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      setStatuses(data);
    } catch (error) {
      console.error('Error fetching record statuses:', error);
    }
  };

  const handleFormChange = (e) => {
    setCurrentStatus({ ...currentStatus, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStatus.status_id) {
      await updateRecordStatus(currentStatus.status_id, currentStatus);
    } else {
      await createRecordStatus(currentStatus);
    }
    setCurrentStatus({ status: '', color: '', description: '' });
    setShowForm(false);
    fetchRecordStatuses();
  };

  const createRecordStatus = async (statusData) => {
    await fetch(`${backendURL}/api/record-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(statusData),
        credentials: 'include'
      });
  };

  const updateRecordStatus = async (statusId, statusData) => {
    await fetch(`${backendURL}/api/record-status/${statusId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(statusData),
        credentials: 'include'
      });
  };

  const deleteRecordStatus = async () => {
    if (statusToDelete) {
        await fetch(`${backendURL}/api/record-status/${statusToDelete}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setStatusToDelete(null);
        setShowModal(false);
        fetchRecordStatuses();
      }
    };

  const editRecordStatus = (status) => {
    setCurrentStatus(status);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentStatus({ status: '', color: '', description: '' });
    setShowForm(false);
  };

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentStatus({ status: '', color: '', description: '' });
    }
    setShowForm(!showForm);
  };

  const statusForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="status">
          <Form.Label>Status</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter status"
            name="status"
            onChange={handleFormChange}
            value={currentStatus.status}
          />
        </Form.Group>
        <Form.Group controlId="color">
          <Form.Label>Color</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter color"
            name="color"
            onChange={handleFormChange}
            value={currentStatus.color}
          />
        </Form.Group>
        <Form.Group controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            placeholder="Enter description"
            name="description"
            onChange={handleFormChange}
            value={currentStatus.description}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Status"
        listLabel="Show Statuses List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Record Statuses"
        form={statusForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="status"
                nounPlural="statuses"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search statuses" placeholder="Status or description" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No statuses have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleStatuses.map((status) => (
          <ExpandableRow
            key={status.status_id}
            main={{ title: status.status, subtitle: status.description || 'No description provided.' }}
            pills={status.color ? [{ label: status.color }] : []}
            actions={(
              <>
                <Button size="sm" onClick={() => editRecordStatus(status)} aria-label="Edit status">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setStatusToDelete(status.status_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete status"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteRecordStatus}
        bodyText="Are you sure you want to delete this status?"
      />
    </div>
  );
};

export default RecordStatusComponent;
